import { z } from "zod";

/**
 * The mandatory reason a recruiter gives before undoing a candidate's status.
 *
 * The bounds and the normalization are the server's — `UNDO_REASON_MIN_LENGTH`
 * and `collapse_whitespace` in `server/app/core/validators.py`, applied by
 * `StatusUndoConfirmationRequest`. The form refuses what the route refuses,
 * with the same limits and the same words, so the recruiter is told here
 * rather than by a 422.
 */
export const UNDO_REASON_MIN_LENGTH = 10;
export const UNDO_REASON_MAX_LENGTH = 1000;

/** Trim the ends, squeeze every internal whitespace run to one space. */
export const normalizeUndoReason = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

export const undoStatusReasonSchema = z.object({
  // Normalize first: the minimum is a promise about what the row will say, and
  // ten spaces say nothing.
  reason: z
    .string()
    .transform(normalizeUndoReason)
    .pipe(
      z
        .string()
        .min(UNDO_REASON_MIN_LENGTH, `Reason must be at least ${UNDO_REASON_MIN_LENGTH} characters`)
        .max(UNDO_REASON_MAX_LENGTH, `Reason must be at most ${UNDO_REASON_MAX_LENGTH} characters`),
    ),
});

export const undoStatusReasonDefaultValues = { reason: "" };

/**
 * The reviewer's note on an undo request.
 *
 * `REVIEW_NOTE_MAX_LENGTH` is `REVIEW_NOTE_MAX_LENGTH` in
 * `server/app/api/v2/undo_request/schema/api.py`, and a rejection's minimum is
 * `UNDO_REASON_MIN_LENGTH` there: a rejection says why, because the requester
 * reads it. Approving may leave the note empty. Normalized like the reason, so
 * the bound measures what the row will say.
 */
export const REVIEW_NOTE_MAX_LENGTH = 1000;
export const REJECT_NOTE_MIN_LENGTH = UNDO_REASON_MIN_LENGTH;

export const undoReviewNoteSchema = z.object({
  note: z
    .string()
    .transform(normalizeUndoReason)
    .pipe(z.string().max(REVIEW_NOTE_MAX_LENGTH, `Note must be at most ${REVIEW_NOTE_MAX_LENGTH} characters`)),
});

export const undoReviewNoteDefaultValues = { note: "" };
